/** Renders `auth_methods` / `client_auth_methods` array values.
 *
 * Each entry shows as a clickable Chip labelled by `name || backend`.
 * Click → opens AuthMethodEditDialog for that entry. "Add method" opens a
 * tiny picker (local | auth0) then routes into the same dialog with a
 * fresh entry.
 */
import { Box, Button, Chip, Menu, MenuItem, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';

import { AuthMethodEditDialog, type AuthMethod } from './AuthMethodEditDialog';

interface Props {
  value: AuthMethod[];
  readOnly?: boolean;
  onChange: (next: AuthMethod[]) => void;
}

function makeBlankMethod(backend: 'local' | 'auth0'): AuthMethod {
  if (backend === 'auth0') {
    return {
      backend: 'auth0',
      name: '',
      domain: '',
      client_id: '',
      allow_signup: false,
      allowed_domains: [],
      access_groups: [],
    };
  }
  return { backend: 'local', name: '' };
}

function labelFor(m: AuthMethod): string {
  return (m.name && m.name.trim()) || m.backend || 'unnamed';
}

export function AuthMethodsCell({ value, readOnly = false, onChange }: Props) {
  const methods = Array.isArray(value) ? value : [];

  // null = closed; {index} = editing existing; {index: -1, draft} = adding new
  const [editing, setEditing] = useState<{ index: number; draft: AuthMethod } | null>(null);

  // Anchor element for the "add" backend picker menu
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);

  function handleSave(next: AuthMethod) {
    if (!editing) return;
    if (editing.index === -1) {
      onChange([...methods, next]);
    } else {
      onChange(methods.map((m, i) => (i === editing.index ? next : m)));
    }
    setEditing(null);
  }

  function handleRemove(idx: number) {
    onChange(methods.filter((_, i) => i !== idx));
  }

  function startAdd(backend: 'local' | 'auth0') {
    setAddAnchor(null);
    setEditing({ index: -1, draft: makeBlankMethod(backend) });
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {methods.map((m, i) => (
        <Tooltip
          key={i}
          title={`${m.backend} — click to edit`}
          placement="top"
          arrow
          disableInteractive
        >
          <Chip
            label={labelFor(m)}
            size="small"
            color={m.backend === 'auth0' ? 'primary' : 'default'}
            variant="outlined"
            onClick={() => setEditing({ index: i, draft: m })}
            onDelete={readOnly ? undefined : () => handleRemove(i)}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
      ))}

      {!readOnly && (
        <>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={(e) => setAddAnchor(e.currentTarget)}
            sx={{ ml: methods.length ? 0.5 : 0, textTransform: 'none', minWidth: 0, py: 0 }}
          >
            Add method
          </Button>
          <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
            <MenuItem onClick={() => startAdd('local')}>Local (username/password)</MenuItem>
            <MenuItem onClick={() => startAdd('auth0')}>Auth0 (SSO)</MenuItem>
          </Menu>
        </>
      )}

      <AuthMethodEditDialog
        open={editing !== null}
        method={editing?.draft ?? null}
        readOnly={readOnly}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    </Box>
  );
}
